import React from "react";
import { ReactComponent as CollectionIcon } from "../../assets/icons/notification-collection.svg";
import { ReactComponent as FailedIcon } from "../../assets/icons/notification-failed.svg";
import { ReactComponent as SuccessIcon } from "../../assets/icons/notification-success.svg";
import { ReactComponent as PendingIcon } from "../../assets/icons/notification-pending.svg";
import { ReactComponent as DownloadIcon } from "../../assets/icons/download-white.svg";
import { ReactComponent as ArrowRightIcon } from "../../assets/icons/arrow-right-sm.svg";
import {
  NotificationCard as Card,
  TypeIcon,
  CardBody,
  CardHeader,
  CardTitle,
  CardSide,
  UnreadDot,
  AmountRow,
  Amount,
  AmountCurrency,
  StatusPill,
  Source,
  Description,
  ActionsRow,
  PrimaryAction,
  SecondaryAction,
  Meta,
  PageMeta,
} from "./styles";
import { NotificationItem, NotificationType } from "./types";

type Props = {
  notification: NotificationItem;
  layout?: "sheet" | "page";
  onDownloadReceipt?: (notification: NotificationItem) => void;
  onViewTransaction?: (notification: NotificationItem) => void;
  onClick?: (notification: NotificationItem) => void;
};

const getTypeIcon = (type: NotificationType) => {
  switch (type) {
    case "collection_received":
    case "payout_completed":
    case "payment_link_success":
    case "settlement_completed":
    case "wire_completed":
      return { icon: <CollectionIcon />, variant: "success" as const };
    case "payout_failed":
    case "transaction_failed":
    case "payment_link_failed":
    case "wire_failed":
      return { icon: <FailedIcon />, variant: "failed" as const };
    case "transaction_completed":
      return { icon: <SuccessIcon />, variant: "success" as const };
    case "transaction_pending":
      return { icon: <PendingIcon />, variant: "pending" as const };
    default: {
      const _exhaustive: never = type;
      return _exhaustive;
    }
  }
};

const NotificationCardItem = ({
  notification,
  layout = "sheet",
  onDownloadReceipt,
  onViewTransaction,
  onClick,
}: Props) => {
  const showDownload =
    notification.actions.includes("download_receipt") &&
    Boolean(onDownloadReceipt);
  const showView =
    notification.actions.includes("view_transaction") &&
    Boolean(onViewTransaction);
  const { icon, variant } = getTypeIcon(notification.type);
  const isPage = layout === "page";
  const metaParts = [
    notification.timeAgo,
    notification.category,
    notification.reference,
  ].filter(Boolean);
  const metaText = metaParts.join(" · ");
  const showAmount = Boolean(notification.amount);
  const showStatus = Boolean(notification.status);

  const handleClick = () => {
    if (!notification.read) {
      onClick?.(notification);
    }
  };

  return (
    <Card
      $unread={!notification.read}
      $layout={layout}
      onClick={handleClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={(event) => {
        if (!onClick) {
          return;
        }

        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          handleClick();
        }
      }}
    >
      <TypeIcon $variant={variant}>{icon}</TypeIcon>
      <CardBody $layout={layout}>
        <CardHeader>
          <CardTitle>{notification.title}</CardTitle>
          {!isPage && !notification.read && (
            <UnreadDot aria-label="Unread" />
          )}
        </CardHeader>

        {(showAmount || showStatus) && (
          <AmountRow>
            {showAmount && (
              <Amount>
                {notification.amount}
                {notification.currency && (
                  <AmountCurrency>{notification.currency}</AmountCurrency>
                )}
              </Amount>
            )}
            {showStatus && notification.status && (
              <StatusPill $status={notification.status}>
                {notification.status}
              </StatusPill>
            )}
          </AmountRow>
        )}

        {notification.source && <Source>{notification.source}</Source>}
        <Description>{notification.description}</Description>

        {(showDownload || showView) && (
          <ActionsRow>
            {showDownload && (
              <PrimaryAction
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  onDownloadReceipt?.(notification);
                }}
              >
                <DownloadIcon />
                Download receipt
              </PrimaryAction>
            )}
            {showView && (
              <SecondaryAction
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  onViewTransaction?.(notification);
                }}
              >
                <ArrowRightIcon />
                View transaction
              </SecondaryAction>
            )}
          </ActionsRow>
        )}

        {!isPage && metaText && <Meta>{metaText}</Meta>}

        {isPage && (
          <CardSide>
            {!notification.read ? (
              <UnreadDot aria-label="Unread" />
            ) : (
              <span />
            )}
            {metaText && <PageMeta>{metaText}</PageMeta>}
          </CardSide>
        )}
      </CardBody>
    </Card>
  );
};

export default NotificationCardItem;
